import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import Context from '../../../utils/context';
import {
  isWithinCallHours,
  formatCallStartInViewerTimezone,
  formatCallHoursRangeInViewerTimezone,
} from '../../../helpers/timezone_utils';
import { trackPhoneCallClick } from '../../../api/activities';
import RequestCallModal from '../../users/conversation_room/request_call_modal';
import ScheduleCallModal from '../../users/conversation_room/schedule_call_modal';
import CallsDrawer from '../../users/conversation_room/calls_drawer';

import styles from './index.module.css';

const CALL_MODAL_REQUEST = 'request';
const CALL_MODAL_SCHEDULE = 'schedule';

const PhoneCall = ({ otherUser, conversationId, activeCall, onCallCreated, currentUserTimezone, incentive = null }) => {
  const rawFirstName = otherUser?.profile?.display_name?.split(' ')[0] || 'your mentor';
  const firstName = rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1);
  const timezone = otherUser?.user?.timezone;
  const callHoursRange = formatCallHoursRangeInViewerTimezone(timezone);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [callModal, setCallModal] = useState(null);
  const [callable, setCallable] = useState(isWithinCallHours(timezone));
  const history = useHistory();
  const context = useContext(Context);
  const isMobile = context.viewport.getIsMobile();
  const currentUser = context.auth.getCurrentUser();
  const isMentor = currentUser?.current_profile_type === 'PatientPartner';

  useEffect(() => {
    const interval = setInterval(() => setCallable(isWithinCallHours(timezone)), 30000);
    return () => clearInterval(interval);
  }, [timezone]);

  if (isMentor) return null;
  if (activeCall) return null;

  const closeDrawer = () => setDrawerOpen(false);
  const closeCallModal = () => setCallModal(null);

  const handleTriggerClick = () => {
    trackPhoneCallClick('conversation_header_call_button', { conversation_id: conversationId });
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setCallModal(CALL_MODAL_REQUEST);
    }
  };

  const handleCallCreated = (call) => {
    closeCallModal();
    closeDrawer();
    if (typeof onCallCreated === 'function') onCallCreated(call);
  };

  const dismissDrawer = () => {
    trackPhoneCallClick('calls_drawer_close', { conversation_id: conversationId });
    closeDrawer();
  };

  const openRequestFromDrawer = () => {
    trackPhoneCallClick('calls_drawer_request_a_call', { conversation_id: conversationId });
    setCallModal(CALL_MODAL_REQUEST);
  };

  const openScheduleFromDrawer = () => {
    trackPhoneCallClick('calls_drawer_schedule_a_call', { conversation_id: conversationId });
    setCallModal(CALL_MODAL_SCHEDULE);
  };

  const openExplainerFromDrawer = () => {
    trackPhoneCallClick('calls_drawer_how_calls_work', { conversation_id: conversationId });
    const returnTo = conversationId ? `/member/conversations/${conversationId}` : '/member/conversations';
    history.push(`/member/phone_calls/mentee?return_to=${encodeURIComponent(returnTo)}`);
  };

  return (
    <>
      <button type='button' className={styles.container} onClick={handleTriggerClick} aria-label={`Call ${firstName}`}>
        <svg
          viewBox='0 0 256 256'
          fill='currentColor'
          xmlns='http://www.w3.org/2000/svg'
          height='20px'
          width='20px'
          style={{ flexShrink: 0 }}
        >
          <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
        </svg>
        {!isMobile && <span className={styles.cta_text}>Call {firstName}</span>}
      </button>

      <CallsDrawer
        open={drawerOpen && !callModal}
        otherUserFirstName={firstName}
        afterHoursLabel={formatCallStartInViewerTimezone(timezone)}
        callable={callable}
        isMentor={isMentor}
        incentive={incentive}
        onClose={dismissDrawer}
        onRequestCall={openRequestFromDrawer}
        onScheduleCall={openScheduleFromDrawer}
        onHowCallsWork={openExplainerFromDrawer}
      />

      <RequestCallModal
        open={callModal === CALL_MODAL_REQUEST}
        otherUserFirstName={firstName}
        afterHoursRange={callHoursRange}
        callable={callable}
        conversationId={conversationId}
        incentive={incentive}
        onClose={closeCallModal}
        onCallCreated={handleCallCreated}
        onScheduleInstead={() => setCallModal(CALL_MODAL_SCHEDULE)}
      />

      <ScheduleCallModal
        open={callModal === CALL_MODAL_SCHEDULE}
        conversationId={conversationId}
        otherUserTimezone={timezone}
        viewerTimezone={currentUserTimezone}
        otherUserFirstName={firstName}
        incentive={incentive}
        onClose={closeCallModal}
        onCallCreated={handleCallCreated}
      />
    </>
  );
};

export default PhoneCall;
